// 2026 AI·디지털 리터러시 결과지 공통 로직.
// 문구/기준값은 reportData.js(엑셀 자동 생성)에서만 가져오고, 여기서는 판정·조회만 한다.
import {
  AREA_CODES,
  AREA_NAMES,
  SUB_AREA_LEVELS,
  DETAIL_AREAS,
  QUESTIONS,
  ACHIEVEMENT_CUTS,
  ACHIEVEMENT_LEVEL_DESC,
  AREA_LEVEL_COMMENTS,
  IMPROVEMENT_MATERIALS,
  EXPECTED_PERFORMANCE,
  CLASS_STRENGTH_WEAKNESS,
  CLASS_ACHIEVEMENT_PHRASES,
} from './reportData';

// 추천 학습자료 QR 이미지 저장소.
// 2025는 .../result/literacy/qr/ 아래였지만 2026 파일은 .../result/literacy/ 바로 아래에 올라가 있다.
export const QR_BASE_URL = 'https://kr.object.gov-ncloudstorage.com/cbt-object/result/literacy/';

const SCH_LEV_LABEL = { e: '초등학교', m: '중학교', h: '고등학교' };

/** wc_school.schul_type('E'|'M'|'H') 또는 소문자 코드를 'e'|'m'|'h'로 정규화. 알 수 없으면 'e'. */
export function toSchLev(schulType) {
  const v = String(schulType || '').trim().toLowerCase();
  if (v === 'e' || v === 'm' || v === 'h') return v;
  if (v.startsWith('elem')) return 'e';
  if (v.startsWith('mid')) return 'm';
  if (v.startsWith('high')) return 'h';
  return 'e';
}

export function schoolTypeLabel(schulType) {
  return SCH_LEV_LABEL[toSchLev(schulType)];
}

export function areaName(code) {
  return AREA_NAMES[AREA_CODES.indexOf(code)] || '';
}

/** code('a'~'d') → field 번호(1~4) */
export function areaIndex(code) {
  return AREA_CODES.indexOf(code) + 1;
}

/** 하위영역명(11개)을 영역 코드별로 묶어 반환. 학교급 무관하게 동일하지만 시트 구조를 따른다. */
export function detailAreasByCode(schulType) {
  const list = DETAIL_AREAS[toSchLev(schulType)] || DETAIL_AREAS.e || [];
  return AREA_CODES.reduce((acc, code) => {
    acc[code] = list.filter((d) => d.code === code).map((d) => d.name);
    return acc;
  }, {});
}

// ---------- 영역별 상/중/하 ----------
const LEVEL_KO_TO_KEY = { 상: 'lev_high', 중: 'lev_mid', 하: 'lev_low' };
const LEVEL_KEY_TO_KO = { lev_high: '상', lev_mid: '중', lev_low: '하' };

/** 영역 점수 → 'lev_high'|'lev_mid'|'lev_low' (하위영역 시트 lev_high/lev_mid 기준점) */
export function areaLevelByScore(schulType, code, score) {
  const th = SUB_AREA_LEVELS[toSchLev(schulType)]?.[code];
  const n = Number(score);
  if (!th || Number.isNaN(n)) return null;
  if (n >= th.high) return 'lev_high';
  if (n >= th.mid) return 'lev_mid';
  return 'lev_low';
}

/**
 * DB에 저장된 한글 수준('상'/'중'/'하')이 있으면 그것을, 없으면 점수로 계산한다.
 * 둘 다 없으면 'lev_mid'.
 */
export function resolveAreaLevelKey(schulType, code, rawLevel, score) {
  if (rawLevel && LEVEL_KO_TO_KEY[String(rawLevel).trim()]) {
    return LEVEL_KO_TO_KEY[String(rawLevel).trim()];
  }
  return areaLevelByScore(schulType, code, score) || 'lev_mid';
}

export function levelKeyToKo(key) {
  return LEVEL_KEY_TO_KO[key] || '';
}

export function areaLevelComment(schulType, code, levelKey) {
  return AREA_LEVEL_COMMENTS[toSchLev(schulType)]?.[code]?.[levelKey] || '';
}

// ---------- 성취수준(1~4) ----------
/** 총점 만점 = 문항분류 시트의 문항 배점 합 */
export function maxTotalScore(schulType) {
  const qs = QUESTIONS[toSchLev(schulType)] || [];
  return qs.reduce((sum, q) => sum + (Number(q.score) || 0), 0);
}

/** 영역별 만점(문항 배점 합) */
export function maxAreaScore(schulType, code) {
  const qs = QUESTIONS[toSchLev(schulType)] || [];
  return qs.filter((q) => q.code === code).reduce((sum, q) => sum + (Number(q.score) || 0), 0);
}

// ---------- 성취수준 구간(원점수) ----------
// 결과지에 보이는 점수는 척도점수로 변환하지 않고 원점수를 그대로 쓴다.
// 구간 기준은 엑셀 '성취수준 기준 점수' 시트(ACHIEVEMENT_CUTS)를 그대로 쓴다.
// (검증: ACHIEVEMENT_CUTS의 4수준 하한은 백엔드 적재 SQL의 lit26_scale.ach_level
//  경계와 원점수 기준으로 정확히 일치한다 — 척도점수는 화면 표시만 바뀔 뿐,
//  수준 판정 자체는 원래도 이 원점수 경계와 같았다.)

/**
 * 성취수준별 원점수 구간 [{level, min, max}] (4→1 순).
 * ACHIEVEMENT_CUTS가 없으면 null — 화면에서는 구간 표기를 생략할 것.
 */
export function achievementRanges(schulType) {
  const lev = toSchLev(schulType);
  const cut = ACHIEVEMENT_CUTS[lev];
  if (!cut || cut['4'] == null) return null;
  const max = maxTotalScore(lev);
  return [
    { level: 4, min: cut['4'], max },
    { level: 3, min: cut['3'], max: cut['4'] - 1 },
    { level: 2, min: cut['2'], max: cut['3'] - 1 },
    { level: 1, min: cut['1'] ?? 0, max: cut['2'] - 1 },
  ];
}

/** 총점(원점수) → 성취수준(1~4). 판정 불가면 null. */
export function achievementLevelByScore(schulType, totalScore) {
  const lev = toSchLev(schulType);
  const n = Number(totalScore);
  if (Number.isNaN(n)) return null;

  const cut = ACHIEVEMENT_CUTS[lev];
  if (!cut || cut['4'] == null) return null;
  if (n >= cut['4']) return 4;
  if (n >= cut['3']) return 3;
  if (n >= cut['2']) return 2;
  return 1;
}

/** DB의 achievement_level을 우선, 없으면 점수로 계산 */
export function resolveAchievementLevel(schulType, rawLevel, totalScore) {
  const n = Number(rawLevel);
  if (n >= 1 && n <= 4) return n;
  return achievementLevelByScore(schulType, totalScore) || 1;
}

export function achievementLevelDesc(level) {
  return ACHIEVEMENT_LEVEL_DESC[String(level)] || '';
}

/** 학교급 × 수준 → 기대 성취 수행 문장 배열 */
export function expectedPerformance(schulType, level) {
  return EXPECTED_PERFORMANCE[toSchLev(schulType)]?.[String(level)] || [];
}

// ---------- 문항 ----------
/** 영역 코드별 문항 목록 (문항번호 오름차순) */
export function questionsByArea(schulType) {
  const qs = QUESTIONS[toSchLev(schulType)] || [];
  return AREA_CODES.reduce((acc, code) => {
    acc[code] = qs.filter((q) => q.code === code);
    return acc;
  }, {});
}

export function findQuestion(schulType, qNo) {
  return (QUESTIONS[toSchLev(schulType)] || []).find((q) => Number(q.q_no) === Number(qNo)) || null;
}

// ---------- 정오표 (tb_exam_result_literacy_detail) ----------
// 컬럼은 m{모듈}_{모듈내번호} 이고 값은 'Y'(정답) / 'N'(오답) / NULL(해당 문항 없음).
// 모듈 6은 중·고에만 있고 채점 대상이 아니라 문항분류 시트에 없으므로 아래 계산에서 자동으로 빠진다.

/** 정오표 행에서 (module, sub_no)로 문항을 찾아 O/X를 돌려준다. 값이 없으면 null. */
export function answerOf(schulType, detail, question) {
  const v = detail?.[`m${question.module}_${question.sub_no}`];
  if (v == null || v === '') return null;
  const s = String(v).trim().toUpperCase();
  if (s === 'Y' || s === 'O' || s === '1') return true;
  if (s === 'N' || s === 'X' || s === '0') return false;
  return null;
}

/** 정오표가 실려 있는지(m1_1 등 컬럼 존재 여부) */
export function hasAnswerDetail(schulType, detail) {
  if (!detail) return false;
  return (QUESTIONS[toSchLev(schulType)] || []).some((q) => answerOf(schulType, detail, q) !== null);
}

/** 정오표 → 틀린 문항번호 배열 */
export function wrongQuestionsFromDetail(schulType, detail) {
  return (QUESTIONS[toSchLev(schulType)] || [])
    .filter((q) => answerOf(schulType, detail, q) === false)
    .map((q) => q.q_no);
}

/** 정오표 → 원점수(맞은 문항 배점 합). 모듈 6은 문항분류에 없어 제외된다. */
export function rawScoreFromDetail(schulType, detail) {
  return (QUESTIONS[toSchLev(schulType)] || [])
    .filter((q) => answerOf(schulType, detail, q) === true)
    .reduce((sum, q) => sum + (Number(q.score) || 0), 0);
}

/** 정오표 → 영역별 원점수 { a, b, c, d } */
export function areaScoresFromDetail(schulType, detail) {
  const qs = QUESTIONS[toSchLev(schulType)] || [];
  return AREA_CODES.reduce((acc, code) => {
    acc[code] = qs
      .filter((q) => q.code === code && answerOf(schulType, detail, q) === true)
      .reduce((sum, q) => sum + (Number(q.score) || 0), 0);
    return acc;
  }, {});
}

// ---------- 향상자료(맞춤 학습 제안) ----------
/** (학교급, 문항번호) → 향상자료 행. code를 주면 영역까지 일치해야 한다. */
export function findMaterial(schulType, qNo, code) {
  const list = IMPROVEMENT_MATERIALS[toSchLev(schulType)] || [];
  return (
    list.find((m) => Number(m.q_no) === Number(qNo) && (!code || m.code === code)) ||
    list.find((m) => Number(m.q_no) === Number(qNo)) ||
    null
  );
}

/** 해당 영역을 다 맞은 학생에게 보여줄 자료('모두 맞춘 학생' 행) */
export function findAllCorrectMaterial(schulType, code) {
  return (IMPROVEMENT_MATERIALS[toSchLev(schulType)] || [])
    .find((m) => m.code === code && m.allCorrect) || null;
}

/**
 * 영역(code)마다 자료를 하나씩 골라 4개를 만든다.
 *  - 틀린 문항이 있으면 그중 "쉬운 문항"(향상자료 order 오름차순 = 전체 정답률 높은 순) 자료
 *  - 그 영역을 다 맞았으면 '모두 맞춘 학생' 자료
 * @param wrongQuestions 틀린 문항번호 배열
 */
export function pickImprovements(schulType, wrongQuestions) {
  const lev = toSchLev(schulType);
  const list = IMPROVEMENT_MATERIALS[lev] || [];
  const wrong = new Set((wrongQuestions || []).map((n) => Number(n)));
  return AREA_CODES
    .map((code) => list.find((m) => m.code === code && !m.allCorrect && wrong.has(Number(m.q_no)))
      || findAllCorrectMaterial(lev, code))
    .filter(Boolean);
}

/**
 * 학생 결과 객체에서 맞춤 학습 제안 목록을 만든다.
 * 1) improvement1~4_type/materials(영역코드/문항번호)가 있으면 그대로 매핑 (적재값 우선)
 * 2) 정오표(m1_1~m6_7)가 실려 있으면 틀린 문항으로 직접 선정
 * 3) wrong_questions(틀린 문항번호 배열)가 있으면 그것으로 선정
 */
export function improvementsFromStudent(student) {
  if (!student) return [];
  const lev = toSchLev(student.schul_type);
  const fromDb = [];
  for (let i = 1; i <= 4; i += 1) {
    const code = student[`improvement${i}_type`];
    if (!code) continue;
    const qNo = student[`improvement${i}_materials`];
    // 문항번호가 없으면 그 영역을 다 맞은 것으로 보고 '모두 맞춘 학생' 자료를 쓴다
    const m = qNo ? findMaterial(lev, qNo, code) : findAllCorrectMaterial(lev, code);
    if (m) fromDb.push(m);
  }
  if (fromDb.length) return fromDb;
  if (hasAnswerDetail(lev, student)) {
    return pickImprovements(lev, wrongQuestionsFromDetail(lev, student));
  }
  if (Array.isArray(student.wrong_questions)) return pickImprovements(lev, student.wrong_questions);
  return [];
}

export function materialQrUrl(material) {
  return material?.qr ? QR_BASE_URL + material.qr : '';
}

// ---------- 학급 ----------
export function classStrengthComment(schulType, code, type /* 's' | 'w' */) {
  return CLASS_STRENGTH_WEAKNESS[toSchLev(schulType)]?.[code]?.[type] || '';
}

/** 3수준 이상 비율(%) → '우수한' | '양호한' | '미흡한' */
export function classAchievementPhrase(highRate) {
  const n = Number(highRate) || 0;
  const hit = CLASS_ACHIEVEMENT_PHRASES.find((p) => n >= p.min);
  return hit ? hit.text : CLASS_ACHIEVEMENT_PHRASES[CLASS_ACHIEVEMENT_PHRASES.length - 1]?.text || '';
}
